import '../css/style.css';
import { createRoot } from 'react-dom/client';
import { useState, useEffect, useRef } from 'react';
import Table from './components/table';
import Filters from './components/filters';
import { useTableConfig } from './components/useTableConfig';

const App = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mounted, setMounted] = useState(false);
    const [showRightShadow, setShowRightShadow] = useState(false);
    const [showLeftShadow, setShowLeftShadow] = useState(false);
    const [description, setDescription] = useState('');
    const scrollRef = useRef(null);

    // Handle scroll shadows visibility
    const updateShadows = () => {
        const element = scrollRef.current;
        if (element) {
            const canScroll = element.scrollWidth > element.clientWidth;
            const isScrolledToEnd = Math.abs(element.scrollWidth - element.clientWidth - element.scrollLeft) < 1;
            const isScrolledToStart = element.scrollLeft === 0;
            
            setShowRightShadow(canScroll && !isScrolledToEnd);
            setShowLeftShadow(canScroll && !isScrolledToStart);
        }
    };

    useEffect(() => {
        // Initial shadow check
        updateShadows();
        // Add resize observer to check shadows on resize
        const resizeObserver = new ResizeObserver(updateShadows);
        if (scrollRef.current) {
            resizeObserver.observe(scrollRef.current);
        }
        return () => resizeObserver.disconnect();
    }, [data]); // Re-run when data changes

    useEffect(() => {
        // Get description from data attribute
        const container = document.querySelector('.merit-container-app');
        if (container) {
            setDescription(container.dataset.description || '');
        }

        // Hide initial loader when React mounts
        const initialLoader = document.querySelector('.initial-loader');
        if (initialLoader) {
            initialLoader.style.display = 'none';
        }
        setMounted(true);

        fetch('/wp-json/meritvarden/v1/merit-values')
            .then(response => response.json())
            .then(meritData => {
                setData(meritData);
                setLoading(false);
                
                // Set initial year filter to latest year
                const years = [...new Set(meritData.map(item => item.year))];
                if (years.length > 0) {
                    const latestYear = Math.max(...years).toString();
                    table.getColumn('year')?.setFilterValue(latestYear);
                }
            })
            .catch(error => {
                console.error('Error fetching merit values:', error);
                setLoading(false);
            });
    }, []);

    const table = useTableConfig(data, [
        { id: 'municipality' },
        { id: 'school' },
        { id: 'program' }
    ]);

    // Don't render anything until React is mounted to prevent flash
    if (!mounted) return null;

    return (
        <div className={`relative p-4 ${!loading ? 'loaded' : ''}`}>
            <Filters table={table} originalData={data} description={description} />
            <div className="relative">
                <div 
                    ref={scrollRef}
                    onScroll={updateShadows}
                    className="overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                >
                    <Table table={table} loading={loading} />
                </div>
                {showLeftShadow && (
                    <div className="absolute top-0 bottom-0 left-0 w-8 bg-gradient-to-r to-transparent pointer-events-none from-black/5"></div>
                )}
                {showRightShadow && (
                    <div className="absolute top-0 right-0 bottom-0 w-8 bg-gradient-to-l to-transparent pointer-events-none from-black/5"></div>
                )}
            </div>
        </div>
    );
};

// Wait for DOM to be ready
document.addEventListener('DOMContentLoaded', () => {
    const containers = document.querySelectorAll('.merit-container-app');
    
    if (containers.length === 0) {
        console.warn('No merit-app container found');
        return;
    }
    
    if (containers.length > 1) {
        console.warn('Multiple merit-app containers found. Initializing the first one.');
    }
    
    const root = createRoot(containers[0]);
    root.render(<App />);
});